import { FC, useState, useCallback } from "react";
import styled from "styled-components";

const Tick = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="14" viewBox="0 0 18 14" fill="none">
    <path
      d="M17 1L5.88337 13L1 7.6"
      stroke="#00ED9D"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const Checkbox: FC<
  React.PropsWithChildren<{
    question?: string;
    values: string[];
    checkedValues: string[];
    handleChange: (checkedValues: string[]) => void;
    isRequired?: boolean;
    chooseOne?: boolean;
    mb?: string;
    labelStyles?: { fontSize?: string; lineHeight?: string; marginRight?: string; color?: string };
  }>
> = ({
  question,
  values,
  checkedValues,
  handleChange,
  isRequired = false,
  chooseOne = false,
  mb = "1rem",
  labelStyles,
}) => {
  const [isSelected, setIsSelected] = useState(
    values.reduce((accumulator, currentValue) => {
      accumulator[currentValue] = checkedValues.includes(currentValue);
      return accumulator;
    }, {}),
  );

  const handleClick = useCallback(
    (value: string) => {
      let resultObj = {};

      if (chooseOne) {
        for (const [key] of Object.keys(isSelected)) {
          resultObj[key] = false;
        }

        resultObj[value] = !isSelected[value];
      } else {
        resultObj = {
          ...isSelected,
          [value]: !isSelected[value],
        };
      }

      const resultArr = Object.entries(resultObj)
        .filter(([_, value]) => !!value)
        .map(([key]) => key);

      setIsSelected(resultObj);
      handleChange(resultArr);
    },
    [isSelected, setIsSelected, handleChange, chooseOne],
  );

  return (
    <Wrapper role={chooseOne ? "radiogroup" : null} mb={mb}>
      {question ? (
        <Question>
          {question}
          {isRequired ? <Asterisk /> : null}
          {chooseOne ? <Span> (Choose one)</Span> : null}
        </Question>
      ) : null}
      {values.map((value) => {
        return (
          <Container
            key={value}
            role={chooseOne ? "radio" : "checkbox"}
            aria-checked={isSelected[value]}
            onKeyPress={(e) => e.key === "Enter" && handleClick(value)}
            onClick={() => handleClick(value)}
            $radioButton={chooseOne}
          >
            <span>
              <Box
                tabIndex={0}
                selected={isSelected[value]}
                onKeyPress={(e) => e.key === "Enter" && handleClick(value)}
                onClick={() => handleClick(value)}
                $radioButton={chooseOne}
              />
              <TickWrapper selected={isSelected[value]}>
                <Tick />
              </TickWrapper>
              <input
                type="checkbox"
                id={value}
                name={value}
                defaultChecked={isSelected[value]}
                hidden
              />
            </span>
            <Label
              onClick={() => handleClick(value)}
              $fontSize={labelStyles?.fontSize}
              $lineHeight={labelStyles?.lineHeight}
              $marginRight={labelStyles?.marginRight}
              $color={labelStyles?.color}
            >
              {value}
            </Label>
          </Container>
        );
      })}
    </Wrapper>
  );
};

const Wrapper = styled.div<{ mb: string }>`
  margin-bottom: ${(p) => p.mb || "1rem"};
`;

const Question = styled.div`
  font-style: normal;
  font-weight: bold;
  font-size: 1.25rem;
  line-height: 2rem;
  letter-spacing: 0.075rem;
  color: #5c5757;
  margin-bottom: 1rem;
`;

const Container = styled.div<{ $radioButton?: boolean }>`
  margin: 0.75rem 0;
  position: relative;
  display: ${(p) => (p.$radioButton ? "flex" : "inline-flex")};
  align-items: center;
  cursor: pointer;
`;

const TickWrapper = styled.div<{ selected?: boolean }>`
  display: ${(p) => (p.selected ? "block" : "none")};
  position: absolute;
  top: 0.8rem;
  left: 0.7rem;
  cursor: pointer;
`;

const Box = styled.div<{ selected?: boolean; $radioButton?: boolean }>`
  display: inline-block;
  background: ${(p) => (p.selected ? "#e4fcf4" : "#ffffff")};
  border: 0.125rem solid ${(p) => (p.selected ? "#00ed9d" : "#D9D9D7")};
  box-sizing: border-box;
  border-radius: ${(p) => (p.$radioButton ? "50%" : "0.5rem")};
  width: 2.5rem;
  height: 2.5rem;
  cursor: pointer;
  margin-right: 1rem;
`;

const Label = styled.label<{
  $fontSize?: string;
  $lineHeight?: string;
  $marginRight?: string;
  $color?: string;
}>`
  display: inline-block;
  font-weight: 400;
  font-size: ${(p) => (p.$fontSize ? p.$fontSize : "1.25rem")};
  line-height: ${(p) => (p.$lineHeight ? p.$lineHeight : "2rem")};
  letter-spacing: 0.075rem;
  margin-right: ${(p) => (p.$marginRight ? p.$marginRight : "1.5rem")};
  color: ${(p) => (p.$color ? p.$color : "#5c5757")};
  cursor: pointer;
`;

const Asterisk = styled.span`
  &:before {
    content: "*";
    color: #ff6666;
    font-weight4 100;
  }
`;

const Span = styled.span`
  font-weight: 400;
  color: #5c5757;
`;
